/**
 * physicsFrame.ts
 * Frame-level physics driver — accumulates delta time and runs fixed steps.
 *
 * Extracted from physicsStep.ts so that file stays under the 300-line limit.
 * NO React imports, NO Reanimated imports, NO async.
 *
 * @module physicsFrame
 */

import { gameState } from './gameState';
import { FIXED_STEP } from './physics';
import { runActiveStep } from './sceneStep';

/**
 * The accumulator carries over sub-step time between frames.
 * Declared in module scope so it persists across frames without being React
 * state (React state would trigger re-renders; this must not).
 * Shared by `runPhysicsFrame` — only one game loop may run at a time.
 */
let accumulator = 0;

/**
 * Reset the physics accumulator to zero.
 * Call whenever a level is (re)initialised so leftover fractional time from a
 * previous session does not trigger a spurious physics step on the first frame.
 */
export function resetAccumulator(): void {
  accumulator = 0;
}

/**
 * Drive one animation frame worth of physics from a plain delta-time value.
 *
 * Used by:
 *   - `gameLoop.ts` via `useGameLoop` (Reanimated frame callback on native)
 *   - `gameLoop.web.ts` / `GameCanvas.web.tsx` (plain RAF on web)
 *
 * Uses the module-level accumulator — only one renderer may be active at a
 * time (native or web, not both).
 *
 * @param deltaMs - Milliseconds since the last frame.
 * @param active  - When false the call is a no-op (pause support).
 */
export function runPhysicsFrame(deltaMs: number, active: boolean): void {
  if (!gameState.isRunning || !active) return;

  // DECISION: 100ms cap prevents spiral-of-death after tab suspension or
  // a long frame spike (≈ 6 missed frames at 60fps).
  const clampedDt = Math.min(deltaMs / 1000, 0.1);
  accumulator += clampedDt;

  while (accumulator >= FIXED_STEP) {
    // DECISION: Dispatch via sceneStep registry so SpaceScene (Phase 4 World 2)
    // can swap in its shmup step without inheriting Nova platformer physics.
    // See ADR-009 (World 2 space shmup as a separate scene type).
    runActiveStep(FIXED_STEP);
    accumulator -= FIXED_STEP;
  }
}
